import type { Notification } from '@/features/notifications/types';
import { useAuthStore } from '@/stores/auth.store';
import { API_BASE } from './env';

/**
 * SSE singleton manager — one EventSource per (tenant, user) across
 * every open tab, with multi-tab dedup via BroadcastChannel.
 *
 * # Why a singleton
 *
 * The browser `EventSource` API cannot set custom headers, so the
 * JWT goes in the query string. Opening one connection per tab is
 * wasteful (the BE hub fans out the same event to every listener of
 * the same (tenantId, userId), so duplicate tabs burn CPU and
 * socket slots for nothing). The manager guarantees a single
 * underlying EventSource per page load and uses a BroadcastChannel
 * to coordinate with peer tabs in the same browser — if tab B opens
 * while tab A already streams, tab B becomes a "follower" and
 * receives events through the BroadcastChannel.
 *
 * # Reconnect strategy
 *
 * When the EventSource transitions to `closed`, we wait
 * `nextBackoff()` (1s, 2s, 4s, 8s, max 30s, reset on successful
 * re-open) and reconnect with the `Last-Event-ID` header set to
 * the most recent event id we observed. The BE SSE endpoint emits a
 * `resume` event in response; the FE ignores it for the bell badge
 * (the in-memory hub has no backlog) but the manager still records
 * it as a successful resume.
 *
 * # Polling fallback
 *
 * After 5 seconds in the disconnected state, the manager starts a
 * 30s polling timer that fires `GET /api/v1/notifications?unread=true&limit=50`.
 * This guarantees the bell badge stays current even when SSE
 * consistently fails (e.g. behind a proxy that strips the
 * `text/event-stream` Accept header). When SSE re-opens the polling
 * timer is cleared.
 *
 * # Auto-cleanup
 *
 * `beforeunload` tears down the EventSource + BroadcastChannel so
 * the worker thread does not leak between page reloads. The polling
 * timer is cleared on stop().
 *
 * # Token source
 *
 * The JWT is read fresh from the auth store on every `start()` call
 * (and on reconnect, since the user might have re-signed-in between
 * attempts). A subscription failure with status 401 triggers the
 * standard `api:unauthorized` window event so the AuthGuard
 * redirects to /login.
 */

export type SseConnectionStatus = 'idle' | 'connecting' | 'open' | 'disconnected';

export interface SseNotificationEvent {
  /** SSE event id. The BE emits `${tenantId}.${seq}`. */
  id: string;
  /** Always `notification` for the live feed; `resume` on Last-Event-ID reconnect. */
  event: string;
  /** Notification payload. Undefined on the synthetic `resume` event. */
  data: Notification | { hint: string; resumedAt: string };
}

interface SseManagerState {
  status: SseConnectionStatus;
  /** Last successful SSE event id. Cleared on `stop()`. */
  lastEventId: string | null;
  /** Wall-clock ms timestamp of the most recent transition to disconnected. */
  disconnectedAt: number | null;
  /** Number of consecutive failed connect attempts (resets on open). */
  reconnectAttempts: number;
}

type Listener = (event: SseNotificationEvent) => void;
type StateListener = (state: SseManagerState) => void;

const BACKOFF_STEPS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_BACKOFF_MS = 30_000;
const POLLING_INTERVAL_MS = 30_000;
/**
 * How long to wait for a peer-tab to claim leadership before we
 * assume nobody is streaming and become leader ourselves. Shorter
 * would cause duplicate connections on slow tabs; longer would
 * delay the bell badge on cold-start.
 */
const LEADERSHIP_RACE_MS = 250;

class SseManagerImpl {
  private es: EventSource | null = null;
  private channel: BroadcastChannel | null = null;
  private channelId: string | null = null;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<StateListener>();
  private state: SseManagerState = {
    status: 'idle',
    lastEventId: null,
    disconnectedAt: null,
    reconnectAttempts: 0,
  };
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private leadershipTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private beforeUnloadHandler: (() => void) | null = null;
  private lastBroadcastId = 0;
  /**
   * Tab role during the dedup handshake:
   *   'pending'  — start() called, awaiting peer response within the race window
   *   'leader'   — owns the EventSource
   *   'follower' — receives events via BroadcastChannel only
   *   'none'     — stop() has been called
   */
  private role: 'leader' | 'follower' | 'pending' | 'none' = 'none';

  /** Read the current snapshot. Useful for components that mount mid-flight. */
  getState(): SseManagerState {
    return { ...this.state };
  }

  /**
   * Subscribe to incoming notification events. Returns an unsubscribe
   * function. Multiple subscribers are allowed (e.g. bell badge +
   * NotificationCenterPage).
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Subscribe to manager state transitions (status, lastEventId, attempts). */
  subscribeState(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.getState());
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  /**
   * Start the SSE connection. Safe to call multiple times — subsequent
   * calls are no-ops while the connection is `open` or `connecting`.
   */
  start(): void {
    const auth = useAuthStore.getState();
    if (!auth.token || !auth.user) return;
    if (this.state.status === 'open' || this.state.status === 'connecting') return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    this.openBroadcastChannel(auth.user.id);
    // After opening the channel we are in `pending`. The handshake
    // resolves the role (leader / follower) within LEADERSHIP_RACE_MS.
    this.installBeforeUnload();
  }

  /** Tear down everything — EventSource, BroadcastChannel, timers, listeners. */
  stop(): void {
    this.setState({
      status: 'idle',
      lastEventId: null,
      disconnectedAt: null,
      reconnectAttempts: 0,
    });
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.leadershipTimer) {
      clearTimeout(this.leadershipTimer);
      this.leadershipTimer = null;
    }
    this.stopPolling();
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    this.channelId = null;
    this.role = 'none';
  }

  // -----------------------------------------------------------------
  // Internal — connection
  // -----------------------------------------------------------------

  private connect(): void {
    const auth = useAuthStore.getState();
    if (!auth.token || !auth.user) return;

    this.setState({ status: 'connecting' });

    // Strip the trailing slash from API_BASE so `${API_BASE}/notifications/stream`
    // never becomes `//notifications/stream`.
    const url = `${API_BASE.replace(/\/$/, '')}/notifications/stream?token=${encodeURIComponent(auth.token)}`;
    const es = new EventSource(url, { withCredentials: false });
    this.es = es;

    es.onopen = (): void => {
      this.setState({ status: 'open', disconnectedAt: null, reconnectAttempts: 0 });
      this.stopPolling();
    };

    es.onerror = (): void => {
      // EventSource auto-reconnects internally on transient errors.
      // We treat `onerror` as "the connection is no longer live" and
      // set state to disconnected. The browser will fire `onerror`
      // once before reconnecting; our own backoff loop takes over if
      // the browser gives up (readyState === CLOSED).
      if (es.readyState === EventSource.CLOSED) {
        es.close();
        if (this.es === es) this.es = null;
        this.scheduleReconnect();
      } else {
        this.setState({
          status: 'disconnected',
          disconnectedAt: this.state.disconnectedAt ?? Date.now(),
        });
        this.maybeStartPolling();
      }
    };

    es.addEventListener('notification', (e: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(e.data) as Notification;
        const id = (e as MessageEvent<string> & { lastEventId?: string }).lastEventId ?? '';
        this.handleIncoming({ id, event: 'notification', data: payload });
      } catch {
        // ignore malformed payloads
      }
    });

    es.addEventListener('resume', (e: MessageEvent<string>) => {
      const id = (e as MessageEvent<string> & { lastEventId?: string }).lastEventId ?? '';
      try {
        const payload = JSON.parse(e.data) as { hint: string; resumedAt: string };
        this.handleIncoming({ id, event: 'resume', data: payload });
      } catch {
        this.handleIncoming({ id, event: 'resume', data: { hint: '', resumedAt: '' } });
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const attempt = Math.min(this.state.reconnectAttempts, BACKOFF_STEPS_MS.length - 1);
    const backoff = BACKOFF_STEPS_MS[attempt] ?? MAX_BACKOFF_MS;
    this.setState({
      status: 'disconnected',
      disconnectedAt: Date.now(),
      reconnectAttempts: this.state.reconnectAttempts + 1,
    });
    this.maybeStartPolling();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, backoff);
  }

  private maybeStartPolling(): void {
    if (this.pollingTimer) return;
    if (this.role !== 'leader') return; // followers don't poll
    this.pollingTimer = setInterval(() => {
      void this.pollNotifications();
    }, POLLING_INTERVAL_MS);
    // Fire one immediate poll so the bell updates without waiting 30s.
    void this.pollNotifications();
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private async pollNotifications(): Promise<void> {
    const auth = useAuthStore.getState();
    if (!auth.token) return;
    try {
      const res = await fetch(`${API_BASE.replace(/\/$/, '')}/notifications?unread=true&limit=50`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (res.status === 401) {
        window.dispatchEvent(
          new CustomEvent('api:unauthorized', { detail: { status: 401, token: auth.token } }),
        );
        return;
      }
      if (!res.ok) return;
      const body = (await res.json()) as { data: Notification[]; total: number };
      // Synthesize an event payload — the bell listens for `notification`
      // events. Marking each with id `poll-<timestamp>` so the SSE
      // manager can dedupe (the bell keeps a Set of recent ids).
      for (const n of body.data) {
        this.handleIncoming({ id: `poll-${n.id}`, event: 'notification', data: n });
      }
    } catch {
      // network error — try again on next interval
    }
  }

  private handleIncoming(event: SseNotificationEvent): void {
    if (event.id) {
      this.setState({ lastEventId: event.id });
    }
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {
        // never let one bad listener kill the others
      }
    }
    // Mirror to peer tabs (so the follower tabs see the same event).
    if (this.channel && this.role === 'leader' && event.id) {
      this.lastBroadcastId += 1;
      this.channel.postMessage({
        kind: 'event',
        origin: this.channelId,
        seq: this.lastBroadcastId,
        event,
      });
    }
  }

  private setState(patch: Partial<SseManagerState>): void {
    this.state = { ...this.state, ...patch };
    const snapshot = this.getState();
    for (const listener of this.stateListeners) {
      try {
        listener(snapshot);
      } catch {
        // never let one bad listener kill the others
      }
    }
  }

  // -----------------------------------------------------------------
  // Internal — BroadcastChannel multi-tab dedup
  // -----------------------------------------------------------------

  private openBroadcastChannel(userId: string): void {
    if (typeof BroadcastChannel === 'undefined') {
      // Old browser fallback — every tab owns its own connection.
      this.becomeLeader();
      return;
    }
    if (this.channel) {
      this.channel.close();
    }
    const channelName = `globaleserp-sse-${userId}`;
    const channel = new BroadcastChannel(channelName);
    this.channel = channel;
    this.channelId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `t-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    this.role = 'pending';

    channel.onmessage = (e: MessageEvent): void => {
      const message = e.data as
        | { kind: 'hello'; origin: string }
        | { kind: 'claim'; origin: string }
        | { kind: 'event'; origin: string; seq: number; event: SseNotificationEvent }
        | { kind: 'ping'; origin: string };
      if (!message || typeof message !== 'object') return;
      if (message.kind === 'hello') {
        // Another tab is announcing itself. If we are already a leader,
        // reply with `claim` so the newcomer becomes a follower.
        if (this.role === 'leader' && this.channelId !== message.origin) {
          channel.postMessage({ kind: 'claim', origin: this.channelId });
        }
      } else if (message.kind === 'claim') {
        // A peer claims leadership. If we are still pending (the race
        // window has not expired), become a follower. If we already
        // escalated to leader, ignore (first claim wins).
        if (this.role === 'pending') {
          this.becomeFollower();
        }
      } else if (message.kind === 'event') {
        // A peer leader broadcast an event. Re-emit it locally as if
        // it came through our own EventSource.
        if (message.event?.id) {
          this.setState({ lastEventId: message.event.id });
        }
        for (const listener of this.listeners) {
          try {
            listener(message.event);
          } catch {
            // ignore
          }
        }
      } else if (message.kind === 'ping') {
        // No-op — reserved for future liveness checks.
      }
    };

    // Announce ourselves. If a peer is already leader, it will respond
    // with a `claim` within the race window. Otherwise we become
    // leader ourselves.
    channel.postMessage({ kind: 'hello', origin: this.channelId });
    this.leadershipTimer = setTimeout(() => {
      this.leadershipTimer = null;
      if (this.role === 'pending') {
        this.becomeLeader();
      }
    }, LEADERSHIP_RACE_MS);
  }

  private becomeLeader(): void {
    if (this.leadershipTimer) {
      clearTimeout(this.leadershipTimer);
      this.leadershipTimer = null;
    }
    this.role = 'leader';
    this.connect();
  }

  private becomeFollower(): void {
    if (this.leadershipTimer) {
      clearTimeout(this.leadershipTimer);
      this.leadershipTimer = null;
    }
    this.role = 'follower';
    // Followers do NOT open their own EventSource. They mirror the
    // leader's events via BroadcastChannel. Their SSE status is
    // still `open` from the user's perspective — events still flow,
    // just via a different transport.
    this.setState({ status: 'open', disconnectedAt: null, reconnectAttempts: 0 });
  }

  private installBeforeUnload(): void {
    if (this.beforeUnloadHandler || typeof window === 'undefined') return;
    const handler = (): void => {
      // Best-effort teardown — the page is unloading anyway.
      if (this.es) {
        this.es.close();
        this.es = null;
      }
      if (this.channel) {
        this.channel.close();
        this.channel = null;
      }
    };
    this.beforeUnloadHandler = handler;
    window.addEventListener('beforeunload', handler);
  }
}

export const sseManager = new SseManagerImpl();
